/**
 * IncomingView — inbound ringing call: caller context + Accept/Reject.
 * Container-agnostic; actions are leader-aware in the lib layer.
 *
 * Aannemen toont een "Verbinden…"-pending-state (voorkomt ook dubbel
 * answer()) en maakt een gefaalde answer zichtbaar via de snapshot-fout
 * (phoneState.lastError) — voorheen bleef de UI dan stil op "rinkelt" staan.
 */

import { useEffect, useState } from 'react';
import { phoneState } from '../../../lib/telephony';
import type { CallRecord } from '../../../lib/telephony';
import { CustomerContextPanel } from '../CustomerContextPanel';

interface Props {
  call: CallRecord;
  onAnswer: () => void;
  onReject: () => void;
}

export function IncomingView({ call, onAnswer, onReject }: Props) {
  const callerDescription = call.customer?.name || call.remoteNumber || 'onbekend nummer';
  // Doel-leg van een doorverbinding: benoem de doorverbinder en het klantnummer,
  // anders is dit niet te onderscheiden van een gewone inkomende call.
  const transfer = call.incomingTransfer ?? null;
  const headline = transfer
    ? transfer.mode === 'warm'
      ? `${transfer.from ?? 'Collega'} wil overleggen over ${callerDescription}`
      : `${transfer.from ?? 'Collega'} verbindt ${callerDescription} door`
    : `Inkomend gesprek van ${callerDescription}`;
  const [answering, setAnswering] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const readError = () => {
      const err = phoneState.getLastError();
      if (err && err.clientCallId === call.clientCallId) {
        setError(err.message);
        setAnswering(false);
      } else {
        setError(null);
      }
    };
    readError();
    return phoneState.subscribe(readError);
  }, [call.clientCallId]);

  const handleAnswer = () => {
    if (answering) return;
    setError(null);
    setAnswering(true);
    onAnswer();
  };

  return (
    <div role="alert" aria-live="assertive">
      <div
        className={
          transfer
            ? 'bg-indigo-50 border-b border-indigo-200 px-3 py-2 flex items-center gap-2 animate-pulse'
            : 'bg-emerald-50 border-b border-emerald-200 px-3 py-2 flex items-center gap-2 animate-pulse'
        }
      >
        <span
          className={`w-2 h-2 rounded-full ${transfer ? 'bg-indigo-500' : 'bg-emerald-500'}`}
          aria-hidden
        />
        <span className={`text-sm font-medium ${transfer ? 'text-indigo-800' : 'text-emerald-800'}`}>
          {headline}
        </span>
        <span
          className={`ml-auto text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${
            transfer ? 'bg-indigo-100 text-indigo-700' : 'bg-emerald-100 text-emerald-700'
          }`}
        >
          {transfer ? 'Doorverbinden' : call.brand}
        </span>
      </div>
      <CustomerContextPanel phone={call.remoteNumber} />
      {error && (
        <div className="mx-3 mb-2 px-2 py-1.5 rounded bg-red-50 border border-red-200 text-xs text-red-700" role="alert">
          {error}
        </div>
      )}
      <div className="p-3 pt-0 flex items-center gap-2">
        <button
          type="button"
          onClick={handleAnswer}
          disabled={answering}
          aria-label={`Gesprek aannemen van ${callerDescription}`}
          className="flex-1 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded disabled:opacity-60 disabled:cursor-wait"
        >
          {answering ? 'Verbinden…' : 'Aannemen (A)'}
        </button>
        <button
          type="button"
          onClick={onReject}
          aria-label={`Gesprek weigeren van ${callerDescription}`}
          className="flex-1 py-2 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded"
        >
          Weigeren (Esc)
        </button>
      </div>
    </div>
  );
}
